// Enhanced Debrief Generator Service with improved target linking
const enhancedDebriefGeneratorService = {
  // Generate a complete debrief for a project
  generateDebrief(project, actions, targets, campaigns = [], blueTeamStats = null, webData = null) {
    const sortedActions = this.sortActionsByTimeline(actions);
    const actionsByPhase = this.categorizeActionsByPhase(sortedActions);
    const statistics = this.calculateStatistics(actions, targets);
    const timeline = this.generateTimeline(sortedActions);
    const findings = this.extractKeyFindings(actions, targets);
    const recommendations = this.generateRecommendations(actions, findings);
    const lessonsLearned = this.extractLessonsLearned(actions);

    // New enhanced features
    const actionsByTarget = this.groupActionsByTarget(actions, targets);
    const mitreAnalysis = this.analyzeMitreTechniques(actions);
    const attackPath = this.analyzeAttackPath(actions, targets);
    const detailedActions = this.prepareDetailedActions(actions, targets);

    return {
      metadata: this.generateMetadata(project),
      executiveSummary: this.generateExecutiveSummary(project, statistics, findings),
      timeline,
      statistics,
      phases: actionsByPhase,
      findings,
      technicalDetails: this.generateTechnicalDetails(actions),
      recommendations,
      lessonsLearned,
      futureConsiderations: this.generateFutureConsiderations(actions, findings),
      appendices: this.generateAppendices(actions, targets),
      // New sections
      actionsByTarget,
      mitreAnalysis,
      attackPath,
      detailedActions,
      webAssessment: this.generateWebAssessment(webData),
      siemImportStats: blueTeamStats ? {
        totalImported: blueTeamStats.siemImported || 0,
        targetMatched: blueTeamStats.targetMatched || 0,
        confirmed: blueTeamStats.siemConfirmed || 0,
        dismissed: blueTeamStats.siemDismissed || 0,
        pendingReview: blueTeamStats.siemPendingReview || 0,
        correlationMatched: blueTeamStats.matched || 0,
        reviewRate: blueTeamStats.siemImported > 0
          ? Math.round(((blueTeamStats.siemConfirmed + blueTeamStats.siemDismissed) / blueTeamStats.siemImported) * 100)
          : 0,
        confirmRate: blueTeamStats.siemImported > 0
          ? Math.round((blueTeamStats.siemConfirmed / blueTeamStats.siemImported) * 100)
          : 0
      } : null
    };
  },

  // Group actions by target with improved linking logic
  groupActionsByTarget(actions, targets) {
    const grouped = {};
    
    // Initialize all targets
    targets.forEach(target => {
      const key = `${target.value} (${target.type})`;
      grouped[key] = {
        target: target,
        type: target.type,
        value: target.value,
        description: target.description,
        actions: [],
        successful: 0,
        detected: 0,
        blocked: 0,
        failed: 0,
        partial: 0,
        unknown: 0,
        techniques: new Set(),
        firstCompromise: null,
        timeline: []
      };
    });
    
    // Group actions - multiple strategies for linking
    actions.forEach(action => {
      const linkedTargets = new Set();
      
      // Strategy 1: Direct target links
      if (action.targets && action.targets.length > 0) {
        action.targets.forEach(targetId => {
          // Handle both string IDs and object references
          const targetIdStr = typeof targetId === 'object' ? targetId._id : targetId;
          const target = targets.find(t => 
            t._id === targetIdStr || 
            t._id === targetId ||
            (typeof targetId === 'object' && t._id === targetId._id)
          );
          if (target) {
            linkedTargets.add(target);
          }
        });
      }
      
      // Strategy 2: Text matching in action title and description
      if (linkedTargets.size === 0) {
        const actionText = `${action.title || ''} ${action.description || ''} ${action.notes || ''}`.toLowerCase();
        
        targets.forEach(target => {
          // Check for exact matches
          if (actionText.includes(target.value.toLowerCase())) {
            linkedTargets.add(target);
          }
          
          // Check for IP addresses without full match (e.g., "192.168.1" in "192.168.1.100")
          if (target.type === 'ip') {
            const ipParts = target.value.split('.');
            if (ipParts.length >= 3) {
              const partialIp = ipParts.slice(0, 3).join('.');
              if (actionText.includes(partialIp)) {
                linkedTargets.add(target);
              }
            }
          }
          
          // Check for hostname variations
          if (target.type === 'hostname') {
            const hostnameParts = target.value.split('.');
            if (hostnameParts.length > 0 && actionText.includes(hostnameParts[0].toLowerCase())) {
              linkedTargets.add(target);
            }
          }
        });
      }
      
      // Add action to all linked targets
      if (linkedTargets.size > 0) {
        linkedTargets.forEach(target => {
          const key = `${target.value} (${target.type})`;
          if (grouped[key]) {
            this.addActionToTarget(grouped[key], action);
          }
        });
      } else {
        // If no targets linked, add to an "Unspecified" group
        const unspecifiedKey = 'Unspecified Targets (mixed)';
        if (!grouped[unspecifiedKey]) {
          grouped[unspecifiedKey] = {
            target: { value: 'Unspecified Targets', type: 'mixed', _id: 'unspecified' },
            type: 'mixed',
            value: 'Unspecified Targets',
            description: 'Actions without specific target assignment',
            actions: [],
            successful: 0,
            detected: 0,
            blocked: 0,
            failed: 0,
            partial: 0,
            unknown: 0,
            techniques: new Set(),
            firstCompromise: null,
            timeline: []
          };
        }
        this.addActionToTarget(grouped[unspecifiedKey], action);
      }
    });
    
    // Sort actions within each target by timestamp
    Object.keys(grouped).forEach(key => {
      grouped[key].actions.sort((a, b) => 
        new Date(a.timestamp) - new Date(b.timestamp)
      );
      grouped[key].timeline.sort((a, b) => 
        new Date(a.timestamp) - new Date(b.timestamp)
      );
    });
    
    return grouped;
  },
  
  // Helper method to add action to target
  addActionToTarget(targetData, action) {
    // Add action details
    const actionInfo = {
      id: action._id,
      title: action.title || 'Untitled Action',
      description: action.description,
      timestamp: action.startTimestamp || action.timestamp || new Date().toISOString(),
      endTime: action.endTimestamp,
      operator: action.operator || 'Unknown',
      result: action.result || 'unknown',
      technique: action.mitreAttackTechnique ?
        `${action.mitreAttackTechnique.id} - ${action.mitreAttackTechnique.name}` : null,
      techniqueId: action.mitreAttackTechnique?.id,
      notes: action.notes,
      evidence: action.evidence,
      originalAction: action
    };
    
    targetData.actions.push(actionInfo);
    targetData.timeline.push({
      timestamp: action.startTimestamp || action.timestamp || new Date().toISOString(),
      event: action.title || 'Untitled Action',
      result: action.result || 'unknown'
    });
    
    // Update statistics - handle all possible result types
    const result = (action.result || 'unknown').toLowerCase();
    switch(result) {
      case 'successful':
      case 'success':
        targetData.successful++;
        break;
      case 'detected':
        targetData.detected++;
        break;
      case 'blocked':
        targetData.blocked++;
        break;
      case 'failed':
      case 'failure':
        targetData.failed++;
        break;
      case 'partial':
        targetData.partial++;
        break;
      default:
        targetData.unknown++;
    }
    
    // Track techniques
    if (action.mitreAttackTechnique) {
      targetData.techniques.add(action.mitreAttackTechnique.id);
    }
    
    // Track first compromise
    if ((result === 'successful' || result === 'success') && !targetData.firstCompromise) {
      targetData.firstCompromise = action.startTimestamp || action.timestamp;
    }
  },

  // Analyze MITRE techniques with severity sorting
  analyzeMitreTechniques(actions) {
    const techniqueMap = new Map();
    
    // Define severity levels for techniques
    const severityLevels = {
      'T1003': 'critical', // Credential Dumping
      'T1078': 'critical', // Valid Accounts
      'T1486': 'critical', // Data Encrypted for Impact
      'T1490': 'critical', // Inhibit System Recovery
      'T1055': 'high',     // Process Injection
      'T1543': 'high',     // Create or Modify System Process
      'T1574': 'high',     // Hijack Execution Flow
      'T1098': 'high',     // Account Manipulation
      'T1021': 'high',     // Remote Services
      'T1053': 'high',     // Scheduled Task/Job
    };
    
    actions.forEach(action => {
      if (action.mitreAttackTechnique) {
        const techniqueId = action.mitreAttackTechnique.id;
        
        if (!techniqueMap.has(techniqueId)) {
          techniqueMap.set(techniqueId, {
            technique: techniqueId,
            name: action.mitreAttackTechnique.name,
            tactic: action.mitreAttackTechnique.tactic,
            severity: severityLevels[techniqueId] || 'medium',
            actions: [],
            attempts: 0,
            successful: 0,
            detected: 0,
            blocked: 0,
            failed: 0,
            partial: 0,
            unknown: 0,
            operators: new Set(),
            targets: new Set(),
            evidence: []
          });
        }
        
        const techData = techniqueMap.get(techniqueId);
        techData.actions.push({
          id: action._id,
          title: action.title || 'Untitled Action',
          result: action.result || 'unknown',
          timestamp: action.startTimestamp || action.timestamp,
          operator: action.operator || 'Unknown',
          evidence: action.evidence || [],
          originalAction: action
        });
        
        techData.attempts++;
        const result = (action.result || 'unknown').toLowerCase();
        switch(result) {
          case 'successful':
          case 'success':
            techData.successful++;
            break;
          case 'detected':
            techData.detected++;
            break;
          case 'blocked':
            techData.blocked++;
            break;
          case 'failed':
          case 'failure':
            techData.failed++;
            break;
          case 'partial':
            techData.partial++;
            break;
          default:
            techData.unknown++;
        }
        
        techData.operators.add(action.operator || 'Unknown');
        
        if (action.targets) {
          action.targets.forEach(t => techData.targets.add(t));
        }
        
        if (action.evidence) {
          techData.evidence.push(...action.evidence);
        }
      }
    });
    
    // Convert to array and calculate success rates
    const techniques = Array.from(techniqueMap.values()).map(tech => ({
      ...tech,
      successRate: tech.attempts > 0 ? tech.successful / tech.attempts : 0,
      detectionRate: tech.attempts > 0 ? (tech.detected + tech.blocked) / tech.attempts : 0,
      operators: Array.from(tech.operators),
      targets: Array.from(tech.targets),
      evidence: [...new Set(tech.evidence)] // Deduplicate evidence
    }));
    
    // Sort by severity then by number of attempts
    const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    techniques.sort((a, b) => {
      const severityDiff = severityOrder[a.severity] - severityOrder[b.severity];
      if (severityDiff !== 0) return severityDiff;
      return b.attempts - a.attempts;
    });
    
    return techniques;
  },

  // Analyze attack path and visualization data
  analyzeAttackPath(actions, targets) {
    const phases = [
      'Reconnaissance',
      'Resource Development',
      'Initial Access',
      'Execution',
      'Persistence',
      'Privilege Escalation',
      'Defense Evasion',
      'Credential Access',
      'Discovery',
      'Lateral Movement',
      'Collection',
      'Command and Control',
      'Exfiltration',
      'Impact'
    ];
    
    const phaseMap = new Map();
    const connections = [];
    const timeline = [];
    
    // Initialize phases
    phases.forEach(phase => {
      phaseMap.set(phase, {
        name: phase,
        actions: [],
        actionCount: 0,
        successfulCount: 0,
        keyActions: [],
        firstOccurrence: null,
        lastOccurrence: null
      });
    });
    
    // Process actions
    const sortedActions = [...actions].sort((a, b) => 
      new Date(a.startTimestamp || a.timestamp) - new Date(b.startTimestamp || b.timestamp)
    );
    
    sortedActions.forEach((action, index) => {
      if (action.mitreAttackTechnique?.tactic) {
        const phase = phaseMap.get(action.mitreAttackTechnique.tactic);
        if (phase) {
          phase.actions.push(action);
          phase.actionCount++;
          
          const result = (action.result || 'unknown').toLowerCase();
          if (result === 'successful' || result === 'success') {
            phase.successfulCount++;
            if (phase.keyActions.length < 3) {
              phase.keyActions.push({ title: action.title || 'Untitled Action', originalAction: action });
            }
          }
          
          const timestamp = action.startTimestamp || action.timestamp;
          if (!phase.firstOccurrence) {
            phase.firstOccurrence = timestamp;
          }
          phase.lastOccurrence = timestamp;
        }
        
        // Track connections between sequential actions
        if (index > 0) {
          const prevAction = sortedActions[index - 1];
          if (prevAction.mitreAttackTechnique?.tactic !== action.mitreAttackTechnique?.tactic) {
            connections.push({
              from: prevAction.mitreAttackTechnique?.tactic,
              to: action.mitreAttackTechnique?.tactic,
              timestamp: action.startTimestamp || action.timestamp
            });
          }
        }
        
        // Add to timeline
        timeline.push({
          timestamp: action.startTimestamp || action.timestamp,
          phase: action.mitreAttackTechnique?.tactic,
          action: action.title || 'Untitled Action',
          result: action.result || 'unknown',
          targets: action.targets || []
        });
      }
    });
    
    // Calculate attack chain completeness
    const executedPhases = Array.from(phaseMap.values()).filter(p => p.actionCount > 0);
    const completeness = Math.round((executedPhases.length / phases.length) * 100);
    
    // Identify critical paths
    const criticalPaths = this.identifyCriticalPaths(sortedActions, targets);
    
    return {
      phases: executedPhases,
      connections,
      timeline,
      completeness,
      totalPhases: phases.length,
      executedPhases: executedPhases.length,
      criticalPaths,
      visualization: {
        nodes: this.generateVisualizationNodes(phaseMap, targets),
        edges: this.generateVisualizationEdges(connections, actions)
      }
    };
  },

  // Prepare detailed actions with all evidence
  prepareDetailedActions(actions, targets) {
    return actions.map(action => {
      const actionTargets = [];
      
      // Get linked targets
      if (action.targets && action.targets.length > 0) {
        action.targets.forEach(tid => {
          const targetIdStr = typeof tid === 'object' ? tid._id : tid;
          const target = targets.find(t => 
            t._id === targetIdStr || 
            t._id === tid ||
            (typeof tid === 'object' && t._id === tid._id)
          );
          if (target) {
            actionTargets.push(target);
          }
        });
      }
      
      return {
        id: action._id,
        title: action.title || 'Untitled Action',
        description: action.description,
        timestamp: action.startTimestamp || action.timestamp || new Date().toISOString(),
        endTimestamp: action.endTimestamp,
        duration: this.calculateDuration(action.startTimestamp || action.timestamp, action.endTimestamp),
        operator: action.operator || 'Unknown',
        result: action.result || 'unknown',
        technique: action.mitreAttackTechnique ?
          `${action.mitreAttackTechnique.id} - ${action.mitreAttackTechnique.name}` : null,
        techniqueDetails: action.mitreAttackTechnique,
        tactic: action.mitreAttackTechnique?.tactic,
        targets: actionTargets.map(t => ({
          value: t.value,
          type: t.type,
          description: t.description
        })),
        notes: action.notes,
        evidence: this.processEvidence(action.evidence),
        severity: this.assessActionSeverity(action),
        tags: this.generateActionTags(action),
        originalAction: action
      };
    }).sort((a, b) => {
      // Sort by timestamp
      return new Date(a.timestamp) - new Date(b.timestamp);
    });
  },

  // Process evidence including screenshots
  processEvidence(evidence) {
    if (!evidence || evidence.length === 0) return [];
    
    return evidence.map((item, index) => {
      if (typeof item === 'string') {
        // Check if it's a base64 image
        if (item.startsWith('data:image')) {
          return {
            type: 'screenshot',
            data: item,
            caption: `Screenshot ${index + 1}`,
            timestamp: new Date().toISOString()
          };
        } else {
          // It's a URL
          return {
            type: 'url',
            url: item,
            caption: `Evidence link ${index + 1}`
          };
        }
      }
      return item; // Already processed
    });
  },

  // Calculate statistics with improved null handling
  calculateStatistics(actions, targets) {
    const results = actions.reduce((acc, action) => {
      const result = action.result || 'unknown';
      acc[result] = (acc[result] || 0) + 1;
      return acc;
    }, {});

    const techniques = new Set();
    const tactics = new Set();
    
    actions.forEach(action => {
      if (action.mitreAttackTechnique) {
        techniques.add(action.mitreAttackTechnique.id);
        tactics.add(action.mitreAttackTechnique.tactic);
      }
    });

    const totalDuration = this.calculateTotalDuration(actions);
    const averageDuration = this.calculateAverageDuration(actions);

    return {
      totalActions: actions.length,
      results,
      successRate: actions.length > 0 ? (results.successful || 0) / actions.length : 0,
      detectionRate: actions.length > 0 ? ((results.detected || 0) + (results.blocked || 0)) / actions.length : 0,
      uniqueTechniques: techniques.size,
      uniqueTactics: tactics.size,
      targetsCompromised: this.getCompromisedTargets(actions, targets).length,
      totalTargets: targets.length,
      totalDuration,
      averageDuration,
      mostUsedTechniques: this.getMostUsedTechniques(actions),
      actionsByOperator: this.groupByOperator(actions)
    };
  },

  // Get compromised targets with improved matching
  getCompromisedTargets(actions, targets) {
    const compromisedIds = new Set();
    const compromisedValues = new Set();
    
    actions.forEach(action => {
      const result = (action.result || '').toLowerCase();
      if (result === 'successful' || result === 'success') {
        // Check linked targets
        if (action.targets && action.targets.length > 0) {
          action.targets.forEach(targetId => {
            const targetIdStr = typeof targetId === 'object' ? targetId._id : targetId;
            compromisedIds.add(targetIdStr);
          });
        }
        
        // Also check for target references in action text
        const actionText = `${action.title || ''} ${action.description || ''}`.toLowerCase();
        targets.forEach(target => {
          if (actionText.includes(target.value.toLowerCase())) {
            compromisedIds.add(target._id);
            compromisedValues.add(target.value);
          }
        });
      }
    });
    
    return targets.filter(t => compromisedIds.has(t._id) || compromisedValues.has(t.value));
  },

  // Identify critical attack paths
  identifyCriticalPaths(actions, targets) {
    const paths = [];
    
    // Path 1: Initial Access -> Privilege Escalation -> Impact
    const initialAccess = actions.find(a => 
      a.mitreAttackTechnique?.tactic === 'Initial Access' && 
      (a.result === 'successful' || a.result === 'success')
    );
    const privEsc = actions.find(a => 
      a.mitreAttackTechnique?.tactic === 'Privilege Escalation' && 
      (a.result === 'successful' || a.result === 'success')
    );
    const impact = actions.find(a => 
      a.mitreAttackTechnique?.tactic === 'Impact' && 
      (a.result === 'successful' || a.result === 'success')
    );
    
    if (initialAccess && privEsc && impact) {
      paths.push({
        name: 'Complete Compromise Path',
        severity: 'critical',
        actions: [initialAccess, privEsc, impact],
        description: 'Attacker achieved initial access, escalated privileges, and caused impact'
      });
    }
    
    // Path 2: Credential Access -> Lateral Movement
    const credAccess = actions.find(a => 
      a.mitreAttackTechnique?.tactic === 'Credential Access' && 
      (a.result === 'successful' || a.result === 'success')
    );
    const lateralMove = actions.find(a => 
      a.mitreAttackTechnique?.tactic === 'Lateral Movement' && 
      (a.result === 'successful' || a.result === 'success')
    );
    
    if (credAccess && lateralMove) {
      paths.push({
        name: 'Lateral Movement Path',
        severity: 'high',
        actions: [credAccess, lateralMove],
        description: 'Attacker obtained credentials and moved laterally through the network'
      });
    }
    
    // Path 3: Persistence + Defense Evasion
    const persistence = actions.filter(a => 
      a.mitreAttackTechnique?.tactic === 'Persistence' && 
      (a.result === 'successful' || a.result === 'success')
    );
    const defenseEvasion = actions.filter(a => 
      a.mitreAttackTechnique?.tactic === 'Defense Evasion' && 
      (a.result === 'successful' || a.result === 'success')
    );
    
    if (persistence.length > 0 && defenseEvasion.length > 0) {
      paths.push({
        name: 'Persistent Access Path',
        severity: 'high',
        actions: [...persistence, ...defenseEvasion],
        description: 'Attacker established persistence and evaded defenses'
      });
    }
    
    return paths;
  },

  // Rest of the methods remain the same...
  generateMetadata(project) {
    return {
      projectName: project.name,
      client: project.client,
      startDate: project.startDate,
      endDate: project.endDate || new Date().toISOString(),
      status: project.status,
      team: project.team || [],
      generatedDate: new Date().toISOString(),
      classification: 'CONFIDENTIAL - CLIENT RESTRICTED'
    };
  },

  generateExecutiveSummary(project, statistics, findings) {
    const summary = {
      overview: `The red team engagement for ${project.client} was conducted to assess the security posture of their infrastructure and identify potential vulnerabilities that could be exploited by malicious actors. This comprehensive assessment simulated real-world attack scenarios to evaluate the effectiveness of existing security controls.`,
      scope: project.description,
      keyStatistics: {
        totalActions: statistics.totalActions,
        successRate: `${Math.round(statistics.successRate * 100)}%`,
        detectionRate: `${Math.round(statistics.detectionRate * 100)}%`,
        criticalFindings: findings.critical.length,
        highFindings: findings.high.length,
        uniqueTechniques: statistics.uniqueTechniques,
        targetsCompromised: `${statistics.targetsCompromised}/${statistics.totalTargets}`
      },
      mainFindings: this.summarizeMainFindings(findings),
      overallRisk: this.assessOverallRisk(findings, statistics)
    };

    return summary;
  },

  sortActionsByTimeline(actions) {
    return [...actions].sort((a, b) => 
      new Date(a.startTimestamp || a.timestamp) - new Date(b.startTimestamp || b.timestamp)
    );
  },

  categorizeActionsByPhase(actions) {
    const phases = {
      reconnaissance: [],
      initialAccess: [],
      execution: [],
      persistence: [],
      privilegeEscalation: [],
      defenseEvasion: [],
      credentialAccess: [],
      discovery: [],
      lateralMovement: [],
      collection: [],
      commandAndControl: [],
      exfiltration: [],
      impact: []
    };

    const tacticToPhase = {
      'Reconnaissance': 'reconnaissance',
      'Resource Development': 'reconnaissance',
      'Initial Access': 'initialAccess',
      'Execution': 'execution',
      'Persistence': 'persistence',
      'Privilege Escalation': 'privilegeEscalation',
      'Defense Evasion': 'defenseEvasion',
      'Credential Access': 'credentialAccess',
      'Discovery': 'discovery',
      'Lateral Movement': 'lateralMovement',
      'Collection': 'collection',
      'Command and Control': 'commandAndControl',
      'Exfiltration': 'exfiltration',
      'Impact': 'impact'
    };

    actions.forEach(action => {
      if (action.mitreAttackTechnique?.tactic) {
        const phase = tacticToPhase[action.mitreAttackTechnique.tactic];
        if (phase && phases[phase]) {
          phases[phase].push(action);
        }
      }
    });

    return phases;
  },

  generateTimeline(actions) {
    return actions.map((action, index) => ({
      sequence: index + 1,
      timestamp: action.startTimestamp || action.timestamp,
      endTime: action.endTimestamp,
      duration: this.calculateDuration(action.startTimestamp || action.timestamp, action.endTimestamp),
      action: action.title || 'Untitled Action',
      technique: action.mitreAttackTechnique
        ? `${action.mitreAttackTechnique.id} - ${action.mitreAttackTechnique.name}`
        : 'N/A',
      operator: action.operator || 'Unknown',
      result: action.result || 'unknown',
      targets: action.targets || [],
      critical: this.isActionCritical(action),
      originalAction: action
    }));
  },

  extractKeyFindings(actions, targets) {
    const findings = {
      critical: [],
      high: [],
      medium: [],
      low: []
    };

    const cleanupKeywords = ['cleanup', 'entfernen', 'remove', 'remediat', 'restore', 'debrief', 'berichterstellung'];

    actions.forEach(action => {
      const result = (action.result || '').toLowerCase();
      if (result === 'successful' || result === 'success') {
        // Skip cleanup/housekeeping actions
        const actionText = `${action.title || ''} ${action.description || ''}`.toLowerCase();
        if (cleanupKeywords.some(kw => actionText.includes(kw))) return;

        const finding = this.analyzeActionForFindings(action, targets);
        if (finding) {
          findings[finding.severity].push(finding);
        }
      }
    });

    const patterns = this.detectAttackPatterns(actions);
    patterns.forEach(pattern => {
      findings[pattern.severity].push(pattern);
    });

    return findings;
  },

  analyzeActionForFindings(action, targets) {
    const criticalTechniques = ['T1003', 'T1078', 'T1486', 'T1490'];
    const highTechniques = ['T1055', 'T1543', 'T1574', 'T1098'];
    
    const techniqueId = action.mitreAttackTechnique?.id;
    
    if (!techniqueId) return null;

    let severity = 'medium';
    let impact = 'Potential security risk identified';
    
    if (criticalTechniques.includes(techniqueId)) {
      severity = 'critical';
      impact = 'Critical security vulnerability exploited';
    } else if (highTechniques.includes(techniqueId)) {
      severity = 'high';
      impact = 'Significant security weakness identified';
    }

    const affectedTargets = this.getAffectedTargets(action, targets);
    const hasCriticalTarget = affectedTargets.some(t => 
      (t.description || '').toLowerCase().includes('domain controller') ||
      (t.description || '').toLowerCase().includes('database') ||
      (t.description || '').toLowerCase().includes('production')
    );

    if (hasCriticalTarget && severity !== 'critical') {
      severity = 'high';
      impact = 'Critical assets were compromised';
    }

    return {
      id: `FIND-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      title: action.title || 'Untitled Action',
      description: action.description || 'No description provided',
      severity,
      impact,
      technique: action.mitreAttackTechnique,
      affectedAssets: affectedTargets.map(t => t.value),
      evidence: action.evidence || [],
      remediation: this.getRemediationForTechnique(techniqueId)
    };
  },

  detectAttackPatterns(actions) {
    const patterns = [];
    const killChainPhases = this.categorizeActionsByPhase(actions);
    const completedPhases = Object.entries(killChainPhases)
      .filter(([phase, actions]) => actions.length > 0)
      .map(([phase]) => phase);
    
    if (completedPhases.length >= 7) {
      patterns.push({
        id: `PATTERN-KC-${Date.now()}`,
        title: 'Complete Kill Chain Execution',
        description: 'The red team successfully executed actions across multiple phases of the cyber kill chain',
        severity: 'critical',
        phases: completedPhases,
        impact: 'Demonstrates ability to conduct end-to-end attack'
      });
    }

    if (killChainPhases.persistence.length > 0 && killChainPhases.lateralMovement.length > 0) {
      patterns.push({
        id: `PATTERN-PLM-${Date.now()}`,
        title: 'Persistent Lateral Movement Capability',
        description: 'Attackers established persistence and demonstrated lateral movement',
        severity: 'high',
        impact: 'Long-term access with ability to spread through network'
      });
    }

    if (killChainPhases.exfiltration.length > 0) {
      patterns.push({
        id: `PATTERN-EXF-${Date.now()}`,
        title: 'Successful Data Exfiltration',
        description: 'Sensitive data was successfully exfiltrated from the environment',
        severity: 'critical',
        impact: 'Potential data breach scenario demonstrated'
      });
    }

    return patterns;
  },

  generateTechnicalDetails(actions) {
    const details = {
      techniques: {},
      tools: [],
      commands: [],
      indicators: []
    };

    actions.forEach(action => {
      if (action.mitreAttackTechnique) {
        const techniqueId = action.mitreAttackTechnique.id;
        if (!details.techniques[techniqueId]) {
          details.techniques[techniqueId] = {
            ...action.mitreAttackTechnique,
            actions: [],
            successRate: 0,
            detectionRate: 0
          };
        }
        details.techniques[techniqueId].actions.push({
          title: action.title || 'Untitled Action',
          result: action.result || 'unknown',
          timestamp: action.startTimestamp || action.timestamp,
          originalAction: action
        });
      }

      if (action.notes) {
        const toolMatches = action.notes.match(/tool[s]?:\s*([^\n]+)/gi);
        const commandMatches = action.notes.match(/command[s]?:\s*([^\n]+)/gi);
        
        if (toolMatches) details.tools.push(...toolMatches);
        if (commandMatches) details.commands.push(...commandMatches);
      }
    });

    Object.values(details.techniques).forEach(technique => {
      const successful = technique.actions.filter(a => 
        a.result === 'successful' || a.result === 'success'
      ).length;
      const detected = technique.actions.filter(a => 
        a.result === 'detected' || a.result === 'blocked'
      ).length;
      
      technique.successRate = technique.actions.length > 0 ? successful / technique.actions.length : 0;
      technique.detectionRate = technique.actions.length > 0 ? detected / technique.actions.length : 0;
    });

    return details;
  },

  generateRecommendations(actions, findings) {
    const recommendations = [];
    const stats = this.analyzeEngagementData(actions);
    
    // 1. Critical Technique-Based Recommendations
    const criticalTechRecs = this.generateCriticalTechniqueRecommendations(actions, stats);
    recommendations.push(...criticalTechRecs);
    
    // 2. Attack Pattern Recommendations
    const patternRecs = this.generatePatternBasedRecommendations(actions, stats);
    recommendations.push(...patternRecs);
    
    // 3. Defense Gap Recommendations
    const defenseGapRecs = this.generateDefenseGapRecommendations(actions, stats);
    recommendations.push(...defenseGapRecs);
    
    // 4. Monitoring and Detection Recommendations
    const monitoringRecs = this.generateMonitoringRecommendations(actions, stats);
    recommendations.push(...monitoringRecs);
    
    // 5. Infrastructure Hardening Recommendations
    const hardeningRecs = this.generateHardeningRecommendations(actions, stats);
    recommendations.push(...hardeningRecs);
    
    // 6. Process and Training Recommendations
    const processRecs = this.generateProcessRecommendations(actions, stats);
    recommendations.push(...processRecs);
    
    // 7. Finding-Specific Recommendations
    const findingRecs = this.generateFindingRecommendations(findings);
    recommendations.push(...findingRecs);
    
    // Remove duplicates and sort by priority
    const uniqueRecs = this.deduplicateRecommendations(recommendations);
    
    return uniqueRecs.sort((a, b) => {
      const priorityOrder = { immediate: 0, high: 1, medium: 2, low: 3 };
      const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
      if (priorityDiff !== 0) return priorityDiff;
      
      // Secondary sort by risk score
      return (b.riskScore || 0) - (a.riskScore || 0);
    });
  },
  
  analyzeEngagementData(actions) {
    const stats = {
      totalActions: actions.length,
      successfulActions: 0,
      detectedActions: 0,
      blockedActions: 0,
      criticalTechniques: new Set(),
      highTechniques: new Set(),
      commonTechniques: new Map(),
      tacticCoverage: new Map(),
      timeAnalysis: {
        mostActiveHours: new Map(),
        longestDwellTime: 0,
        quickestCompromise: Infinity
      },
      targetAnalysis: {
        criticalAssetsCompromised: 0,
        lateralMovementPaths: 0,
        privilegeEscalations: 0
      },
      detectionAnalysis: {
        detectionRate: 0,
        meanTimeToDetect: 0,
        undetectedCritical: 0
      }
    };
    
    // Analyze each action
    actions.forEach(action => {
      const result = (action.result || '').toLowerCase();
      
      // Count results
      if (result === 'successful' || result === 'success') {
        stats.successfulActions++;
      } else if (result === 'detected') {
        stats.detectedActions++;
      } else if (result === 'blocked') {
        stats.blockedActions++;
      }
      
      // Track techniques
      if (action.mitreAttackTechnique) {
        const techniqueId = action.mitreAttackTechnique.id;
        const tactic = action.mitreAttackTechnique.tactic;
        
        // Count technique usage
        stats.commonTechniques.set(techniqueId, 
          (stats.commonTechniques.get(techniqueId) || 0) + 1
        );
        
        // Track tactic coverage
        stats.tacticCoverage.set(tactic,
          (stats.tacticCoverage.get(tactic) || 0) + 1
        );
        
        // Categorize by severity
        const criticalTechs = ['T1003', 'T1078', 'T1486', 'T1490', 'T1489'];
        const highTechs = ['T1055', 'T1543', 'T1574', 'T1098', 'T1021', 'T1053'];
        
        if (criticalTechs.includes(techniqueId) && (result === 'successful' || result === 'success')) {
          stats.criticalTechniques.add(techniqueId);
        }
        if (highTechs.includes(techniqueId) && (result === 'successful' || result === 'success')) {
          stats.highTechniques.add(techniqueId);
        }
        
        // Track specific attack types
        if (tactic === 'Lateral Movement') {
          stats.targetAnalysis.lateralMovementPaths++;
        }
        if (tactic === 'Privilege Escalation' && (result === 'successful' || result === 'success')) {
          stats.targetAnalysis.privilegeEscalations++;
        }
      }
      
      // Time analysis
      if (action.startTimestamp) {
        const hour = new Date(action.startTimestamp).getHours();
        stats.timeAnalysis.mostActiveHours.set(hour,
          (stats.timeAnalysis.mostActiveHours.get(hour) || 0) + 1
        );
      }
    });
    
    // Calculate detection metrics
    stats.detectionAnalysis.detectionRate = 
      (stats.detectedActions + stats.blockedActions) / stats.totalActions;
    
    stats.detectionAnalysis.undetectedCritical = 
      Array.from(stats.criticalTechniques).length;
    
    return stats;
  },
  
  generateCriticalTechniqueRecommendations(actions, stats) {
    const recommendations = [];
    
    // Check for credential dumping
    if (stats.criticalTechniques.has('T1003')) {
      const credDumpActions = actions.filter(a => 
        a.mitreAttackTechnique?.id === 'T1003' && 
        (a.result === 'successful' || a.result === 'success')
      );
      
      recommendations.push({
        id: 'REC-CRED-001',
        priority: 'immediate',
        category: 'credential_protection',
        title: 'Implement Credential Protection Measures',
        description: `Critical: ${credDumpActions.length} successful credential dumping attempts were executed. Immediate implementation of credential protection is required.`,
        detailedSteps: [
          'Enable Windows Credential Guard on all domain-joined systems',
          'Implement LSASS process protection (RunAsPPL)',
          'Deploy EDR solution with memory protection capabilities',
          'Enable enhanced auditing for LSASS access (Event ID 4656, 4663)',
          'Implement privileged access workstations (PAWs) for administrative tasks'
        ],
        effort: 'high',
        impact: 'critical',
        riskScore: 95,
        timeframe: '1-2 weeks',
        affectedSystems: credDumpActions.length,
        references: [
          'https://docs.microsoft.com/en-us/windows/security/identity-protection/credential-guard/',
          'MITRE ATT&CK: T1003 Mitigations'
        ]
      });
    }
    
    // Check for valid accounts abuse
    if (stats.criticalTechniques.has('T1078')) {
      recommendations.push({
        id: 'REC-AUTH-001',
        priority: 'immediate',
        category: 'authentication',
        title: 'Strengthen Authentication and Account Security',
        description: 'Valid accounts were successfully compromised and abused. Multi-factor authentication and account monitoring must be enhanced.',
        detailedSteps: [
          'Enforce MFA for all user accounts, especially privileged accounts',
          'Implement risk-based conditional access policies',
          'Deploy impossible travel and anomalous sign-in detection',
          'Enable account lockout policies and monitor failed authentication attempts',
          'Implement just-in-time (JIT) access for administrative privileges',
          'Review and remove unnecessary service accounts'
        ],
        effort: 'medium',
        impact: 'critical',
        riskScore: 90,
        timeframe: '2-3 weeks',
        metrics: {
          currentMFACoverage: 'Unknown - Audit Required',
          recommendedMFACoverage: '100% for privileged, 95% for standard users'
        }
      });
    }
    
    // Check for ransomware techniques
    if (stats.criticalTechniques.has('T1486') || stats.criticalTechniques.has('T1490')) {
      recommendations.push({
        id: 'REC-RANSOM-001',
        priority: 'immediate',
        category: 'ransomware_protection',
        title: 'Implement Ransomware Protection Strategy',
        description: 'Ransomware simulation techniques were successful, indicating vulnerability to data encryption attacks.',
        detailedSteps: [
          'Implement immutable backups with air-gapped copies',
          'Enable ransomware protection features in Windows Defender',
          'Implement application control/whitelisting for critical systems',
          'Deploy deception technology (honey files) for early detection',
          'Restrict administrative shares and unnecessary network protocols',
          'Implement network segmentation to limit blast radius',
          'Create and test incident response playbooks for ransomware'
        ],
        effort: 'high',
        impact: 'critical',
        riskScore: 95,
        timeframe: '2-4 weeks',
        estimatedDataAtRisk: 'All production data systems'
      });
    }
    
    return recommendations;
  },
  
  generatePatternBasedRecommendations(actions, stats) {
    const recommendations = [];
    
    // Check for complete kill chain
    if (stats.tacticCoverage.size >= 7) {
      const killChainRec = {
        id: 'REC-KC-001',
        priority: 'high',
        category: 'defense_in_depth',
        title: 'Implement Defense-in-Depth Strategy',
        description: `A complete attack chain was demonstrated across ${stats.tacticCoverage.size} MITRE ATT&CK tactics. Multiple defensive layers must be implemented.`,
        detailedSteps: [
          'Implement network segmentation with strict east-west traffic filtering',
          'Deploy deception technology across all network segments',
          'Implement zero-trust architecture principles',
          'Enhance endpoint detection and response (EDR) coverage to 100%',
          'Implement security orchestration for automated response',
          'Establish 24/7 security operations center (SOC) monitoring'
        ],
        effort: 'high',
        impact: 'critical',
        riskScore: 85,
        timeframe: '3-6 months',
        attackChainPhases: Array.from(stats.tacticCoverage.keys())
      };
      recommendations.push(killChainRec);
    }
    
    // Check for lateral movement patterns
    if (stats.targetAnalysis.lateralMovementPaths > 3) {
      recommendations.push({
        id: 'REC-LATMOV-001',
        priority: 'high',
        category: 'network_security',
        title: 'Restrict Lateral Movement Capabilities',
        description: `${stats.targetAnalysis.lateralMovementPaths} lateral movement paths were identified. Network segmentation and access controls need strengthening.`,
        detailedSteps: [
          'Implement micro-segmentation using host-based firewalls',
          'Deploy privileged access management (PAM) solution',
          'Restrict and monitor SMB, RDP, and WinRM protocols',
          'Implement local admin password solution (LAPS)',
          'Enable restrictive PowerShell execution policies',
          'Deploy network access control (NAC) for device authentication',
          'Monitor and alert on abnormal internal network connections'
        ],
        effort: 'high',
        impact: 'high',
        riskScore: 80,
        timeframe: '2-3 months',
        affectedProtocols: ['SMB', 'RDP', 'WMI', 'PowerShell Remoting']
      });
    }
    
    // Check for persistence mechanisms
    const persistenceTactics = Array.from(stats.tacticCoverage.entries())
      .filter(([tactic, count]) => tactic === 'Persistence' && count > 0);
    
    if (persistenceTactics.length > 0) {
      recommendations.push({
        id: 'REC-PERSIST-001',
        priority: 'high',
        category: 'endpoint_security',
        title: 'Harden Against Persistence Mechanisms',
        description: 'Multiple persistence techniques were successfully deployed. System hardening and monitoring must be enhanced.',
        detailedSteps: [
          'Implement application control to prevent unauthorized binary execution',
          'Monitor and restrict scheduled task creation',
          'Audit and monitor registry run keys and startup folders',
          'Implement file integrity monitoring (FIM) for critical system files',
          'Restrict WMI event subscriptions and monitor WMI activity',
          'Regular audit of service creation and modifications',
          'Implement boot integrity validation'
        ],
        effort: 'medium',
        impact: 'high',
        riskScore: 75,
        timeframe: '3-4 weeks',
        persistenceVectors: ['Scheduled Tasks', 'Registry Keys', 'Services', 'WMI']
      });
    }
    
    return recommendations;
  },
  
  generateDefenseGapRecommendations(actions, stats) {
    const recommendations = [];
    
    // Low detection rate
    if (stats.detectionAnalysis.detectionRate < 0.3) {
      recommendations.push({
        id: 'REC-DETECT-001',
        priority: 'high',
        category: 'detection',
        title: 'Enhance Detection Capabilities',
        description: `Only ${Math.round(stats.detectionAnalysis.detectionRate * 100)}% of attacks were detected. Significant detection improvements are required.`,
        detailedSteps: [
          'Deploy or upgrade EDR solution with behavioral analytics',
          'Implement SIEM with custom detection rules based on this engagement',
          'Enable comprehensive Windows security auditing',
          'Implement network traffic analysis (NTA) solution',
          'Deploy user and entity behavior analytics (UEBA)',
          'Create custom IoCs based on attacker techniques observed',
          'Establish threat hunting program with dedicated resources'
        ],
        effort: 'high',
        impact: 'high',
        riskScore: 85,
        timeframe: '1-3 months',
        currentDetectionRate: `${Math.round(stats.detectionAnalysis.detectionRate * 100)}%`,
        targetDetectionRate: '80%+',
        detectionGapAreas: this.identifyDetectionGaps(actions)
      });
    }
    
    // Undetected critical techniques
    if (stats.detectionAnalysis.undetectedCritical > 0) {
      recommendations.push({
        id: 'REC-CRITICAL-DETECT-001',
        priority: 'immediate',
        category: 'detection',
        title: 'Address Critical Detection Blind Spots',
        description: `${stats.detectionAnalysis.undetectedCritical} critical attack techniques went undetected. Immediate detection enhancements required.`,
        detailedSteps: [
          'Implement specific detections for observed critical techniques',
          'Deploy honey tokens and deception for credential theft detection',
          'Enable PowerShell script block logging and analysis',
          'Implement process injection detection capabilities',
          'Monitor for abnormal LSASS access patterns',
          'Deploy canary files for ransomware detection',
          'Implement real-time file integrity monitoring'
        ],
        effort: 'medium',
        impact: 'critical',
        riskScore: 90,
        timeframe: '1-2 weeks',
        criticalTechniques: Array.from(stats.criticalTechniques),
        specificDetections: this.getDetectionRulesForTechniques(stats.criticalTechniques)
      });
    }
    
    return recommendations;
  },
  
  generateMonitoringRecommendations(actions, stats) {
    const recommendations = [];
    
    // Time-based analysis
    const peakHours = Array.from(stats.timeAnalysis.mostActiveHours.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([hour]) => hour);
    
    if (peakHours.length > 0) {
      recommendations.push({
        id: 'REC-MONITOR-TIME-001',
        priority: 'medium',
        category: 'monitoring',
        title: 'Enhance Monitoring During Peak Attack Hours',
        description: `Attacks were most frequent during hours: ${peakHours.map(h => `${h}:00`).join(', ')}. Monitoring should be enhanced during these periods.`,
        detailedSteps: [
          'Increase SOC analyst coverage during identified peak hours',
          'Implement automated response playbooks for common attacks',
          'Lower detection thresholds during high-risk time windows',
          'Schedule security tool updates outside of peak attack windows',
          'Implement real-time dashboard for security metrics',
          'Create on-call escalation procedures for critical alerts'
        ],
        effort: 'low',
        impact: 'medium',
        riskScore: 60,
        timeframe: '1 week',
        peakAttackHours: peakHours.map(h => `${h}:00-${h+1}:00`),
        recommendedStaffing: 'Increase by 50% during peak hours'
      });
    }
    
    // Monitoring coverage gaps
    recommendations.push({
      id: 'REC-MONITOR-COVER-001',
      priority: 'high',
      category: 'monitoring',
      title: 'Implement Comprehensive Security Monitoring',
      description: 'Current monitoring failed to detect several attack techniques. Comprehensive monitoring strategy needed.',
      detailedSteps: [
        'Implement centralized log collection from all critical systems',
        'Enable PowerShell module logging and script block logging',
        'Configure Sysmon with high-fidelity ruleset',
        'Implement DNS query logging and analysis',
        'Enable command line process auditing',
        'Deploy network packet capture at critical network junctions',
        'Implement file access auditing for sensitive data',
        'Enable authentication logging across all systems'
      ],
      effort: 'medium',
      impact: 'high',
      riskScore: 75,
      timeframe: '2-4 weeks',
      requiredLogSources: [
        'Windows Security Events',
        'PowerShell Logs',
        'Sysmon',
        'DNS Logs',
        'Firewall Logs',
        'Proxy Logs',
        'Application Logs'
      ],
      estimatedLogVolume: '500GB-1TB per day',
      retentionRequirement: '90 days minimum'
    });
    
    return recommendations;
  },
  
  generateHardeningRecommendations(actions, stats) {
    const recommendations = [];
    
    // Check for successful privilege escalation
    if (stats.targetAnalysis.privilegeEscalations > 0) {
      recommendations.push({
        id: 'REC-PRIVESC-001',
        priority: 'high',
        category: 'system_hardening',
        title: 'Prevent Privilege Escalation Attacks',
        description: `${stats.targetAnalysis.privilegeEscalations} successful privilege escalation attacks occurred. System hardening required.`,
        detailedSteps: [
          'Remove users from local administrators group',
          'Implement User Account Control (UAC) at highest level',
          'Disable or restrict PowerShell for standard users',
          'Patch all systems for known privilege escalation vulnerabilities',
          'Implement application control to prevent exploit execution',
          'Remove unnecessary privileges from service accounts',
          'Implement and enforce secure baseline configurations',
          'Regular privilege access reviews and certification'
        ],
        effort: 'medium',
        impact: 'high',
        riskScore: 80,
        timeframe: '2-3 weeks',
        vulnerableConfigurations: [
          'Excessive local admin rights',
          'Weak service account permissions',
          'Unpatched vulnerabilities',
          'Misconfigured UAC settings'
        ]
      });
    }
    
    // General hardening based on success rate
    if (stats.successfulActions / stats.totalActions > 0.7) {
      recommendations.push({
        id: 'REC-HARDEN-001',
        priority: 'high',
        category: 'system_hardening',
        title: 'Implement Comprehensive System Hardening',
        description: `${Math.round((stats.successfulActions / stats.totalActions) * 100)}% attack success rate indicates insufficient system hardening.`,
        detailedSteps: [
          'Implement CIS or DISA STIG security benchmarks',
          'Disable unnecessary services and protocols',
          'Implement host-based firewalls with strict rules',
          'Remove or disable legacy protocols (SMBv1, LLMNR, NetBIOS)',
          'Implement secure PowerShell configurations',
          'Harden RDP configurations and use RDP gateways',
          'Regular vulnerability scanning and patching program',
          'Implement secure boot and code integrity policies'
        ],
        effort: 'high',
        impact: 'high',
        riskScore: 75,
        timeframe: '1-2 months',
        hardeningStandards: ['CIS Controls', 'NIST 800-53', 'DISA STIGs'],
        prioritySystems: ['Domain Controllers', 'High-Value Targets', 'Internet-Facing Systems']
      });
    }
    
    return recommendations;
  },
  
  generateProcessRecommendations(actions, stats) {
    const recommendations = [];
    
    // Incident response improvements
    if (stats.detectedActions > 0 && stats.successfulActions > stats.detectedActions) {
      recommendations.push({
        id: 'REC-IR-001',
        priority: 'medium',
        category: 'process',
        title: 'Enhance Incident Response Procedures',
        description: 'Attacks were detected but not effectively contained. Incident response processes need improvement.',
        detailedSteps: [
          'Develop automated response playbooks for common attacks',
          'Implement security orchestration (SOAR) platform',
          'Create and test incident response runbooks',
          'Establish clear escalation procedures',
          'Implement automated containment for confirmed threats',
          'Regular tabletop exercises and IR drills',
          'Establish threat intelligence program',
          'Create forensics and evidence collection procedures'
        ],
        effort: 'medium',
        impact: 'medium',
        riskScore: 65,
        timeframe: '4-6 weeks',
        metrics: {
          currentMTTR: 'Not measured',
          targetMTTR: '< 30 minutes for critical incidents',
          currentContainmentRate: `${Math.round((stats.blockedActions / stats.detectedActions) * 100)}%`,
          targetContainmentRate: '95%+'
        }
      });
    }
    
    // Security awareness training
    const socialEngineering = actions.filter(a => 
      a.mitreAttackTechnique?.id === 'T1566' || // Phishing
      a.mitreAttackTechnique?.id === 'T1204'    // User Execution
    ).length;
    
    if (socialEngineering > 0) {
      recommendations.push({
        id: 'REC-TRAIN-001',
        priority: 'medium',
        category: 'training',
        title: 'Implement Security Awareness Training Program',
        description: `${socialEngineering} social engineering attacks were successful. User training is required.`,
        detailedSteps: [
          'Implement regular phishing simulation campaigns',
          'Mandatory security awareness training for all staff',
          'Role-based security training for high-risk users',
          'Create security champion program',
          'Regular security newsletters and updates',
          'Gamification of security awareness',
          'Incident reporting training and clear procedures',
          'Clean desk policy and physical security training'
        ],
        effort: 'low',
        impact: 'medium',
        riskScore: 60,
        timeframe: '2-4 weeks to launch',
        trainingMetrics: {
          targetCompletion: '95% within 30 days',
          phishingTestFrequency: 'Monthly',
          targetPhishingClickRate: '< 5%'
        }
      });
    }
    
    return recommendations;
  },
  
  generateFindingRecommendations(findings) {
    const recommendations = [];
    
    // Process critical findings
    findings.critical.forEach((finding, index) => {
      if (finding.remediation) {
        recommendations.push({
          id: `REC-FINDING-CRIT-${index + 1}`,
          priority: 'immediate',
          category: 'vulnerability_remediation',
          title: `Critical: ${finding.title}`,
          description: finding.description,
          detailedSteps: this.expandRemediationSteps(finding.remediation),
          effort: 'high',
          impact: 'critical',
          riskScore: 95,
          timeframe: 'Immediate - within 72 hours',
          affectedAssets: finding.affectedAssets,
          evidence: finding.evidence,
          specificMitigations: this.getTechniqueMitigations(finding.technique)
        });
      }
    });
    
    // Process high findings
    findings.high.forEach((finding, index) => {
      if (finding.remediation) {
        recommendations.push({
          id: `REC-FINDING-HIGH-${index + 1}`,
          priority: 'high',
          category: 'vulnerability_remediation',
          title: `High: ${finding.title}`,
          description: finding.description,
          detailedSteps: this.expandRemediationSteps(finding.remediation),
          effort: 'medium',
          impact: 'high',
          riskScore: 80,
          timeframe: '1-2 weeks',
          affectedAssets: finding.affectedAssets || [],
          evidence: finding.evidence || []
        });
      }
    });
    
    return recommendations;
  },
  
  deduplicateRecommendations(recommendations) {
    const seen = new Map();
    const unique = [];
    
    recommendations.forEach(rec => {
      // Create a key based on category and main content
      const key = `${rec.category}-${rec.title.toLowerCase()}`;
      
      if (!seen.has(key)) {
        seen.set(key, rec);
        unique.push(rec);
      } else {
        // Merge details if similar recommendation exists
        const existing = seen.get(key);
        if (rec.detailedSteps && existing.detailedSteps) {
          existing.detailedSteps = [...new Set([...existing.detailedSteps, ...rec.detailedSteps])];
        }
        if (rec.riskScore > existing.riskScore) {
          existing.riskScore = rec.riskScore;
        }
        if (rec.priority === 'immediate' && existing.priority !== 'immediate') {
          existing.priority = 'immediate';
        }
      }
    });
    
    return unique;
  },
  
  // Helper methods for recommendations
  identifyDetectionGaps(actions) {
    const undetectedTactics = new Set();
    
    actions.forEach(action => {
      const result = (action.result || '').toLowerCase();
      if ((result === 'successful' || result === 'success') && action.mitreAttackTechnique) {
        undetectedTactics.add(action.mitreAttackTechnique.tactic);
      }
    });
    
    return Array.from(undetectedTactics);
  },
  
  getDetectionRulesForTechniques(techniques) {
    const rules = [];
    
    techniques.forEach(techId => {
      const ruleMap = {
        'T1003': [
          'Alert on LSASS access from non-system processes',
          'Monitor for credential dumping tools (mimikatz patterns)',
          'Alert on suspicious process access to LSASS',
          'Monitor SAM database access'
        ],
        'T1078': [
          'Alert on impossible travel scenarios',
          'Monitor for concurrent sessions from different locations',
          'Alert on logins from new/unusual devices',
          'Monitor service account interactive logins'
        ],
        'T1055': [
          'Alert on process injection behaviors',
          'Monitor for CreateRemoteThread API calls',
          'Alert on suspicious cross-process handle requests',
          'Monitor for SetWindowsHookEx usage'
        ],
        'T1021': [
          'Monitor RDP connections from unusual sources',
          'Alert on SMB/Admin share access patterns',
          'Monitor WMI remote execution',
          'Track PowerShell remoting usage'
        ]
      };
      
      if (ruleMap[techId]) {
        rules.push({
          technique: techId,
          detectionRules: ruleMap[techId]
        });
      }
    });
    
    return rules;
  },
  
  expandRemediationSteps(remediation) {
    // Expand brief remediation into detailed steps
    const steps = [remediation];
    
    // Add common additional steps based on keywords
    if (remediation.toLowerCase().includes('mfa')) {
      steps.push(
        'Audit all accounts for MFA enrollment status',
        'Implement conditional access requiring MFA from untrusted networks',
        'Use phishing-resistant MFA methods (FIDO2, Windows Hello)'
      );
    }
    
    if (remediation.toLowerCase().includes('patch')) {
      steps.push(
        'Conduct vulnerability assessment to identify all affected systems',
        'Test patches in non-production environment first',
        'Implement automated patch management solution',
        'Create patch compliance reporting'
      );
    }
    
    if (remediation.toLowerCase().includes('monitor')) {
      steps.push(
        'Configure real-time alerting for suspicious activities',
        'Implement log retention policy (minimum 90 days)',
        'Create correlation rules for attack patterns',
        'Establish baseline of normal activity'
      );
    }
    
    return steps;
  },
  
  getTechniqueMitigations(technique) {
    if (!technique) return [];
    
    const mitigationMap = {
      'T1003': {
        controls: ['M1027', 'M1026', 'M1025', 'M1043'],
        descriptions: [
          'Enable Credential Guard',
          'Restrict LSASS process access',
          'Enable Protected Process Light for LSASS',
          'Monitor and alert on credential access'
        ]
      },
      'T1078': {
        controls: ['M1036', 'M1032', 'M1026'],
        descriptions: [
          'Implement strong password policies',
          'Enable multi-factor authentication',
          'Monitor account usage patterns',
          'Implement privileged access management'
        ]
      },
      'T1055': {
        controls: ['M1040', 'M1026'],
        descriptions: [
          'Enable Exploit Protection',
          'Implement application control',
          'Monitor process creation and injection',
          'Use EDR with anti-injection capabilities'
        ]
      }
    };
    
    const mitigation = mitigationMap[technique.id];
    return mitigation ? mitigation.descriptions : [];
  },

  extractLessonsLearned(actions) {
    const lessons = {
      successes: [],
      challenges: [],
      improvements: []
    };

    const successfulTechniques = new Map();
    actions.forEach(action => {
      const result = (action.result || '').toLowerCase();
      if ((result === 'successful' || result === 'success') && action.mitreAttackTechnique) {
        const technique = action.mitreAttackTechnique.id;
        successfulTechniques.set(technique, 
          (successfulTechniques.get(technique) || 0) + 1
        );
      }
    });

    Array.from(successfulTechniques.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .forEach(([technique, count]) => {
        lessons.successes.push({
          type: 'technique',
          description: `Technique ${technique} was highly effective (${count} successful uses)`,
          recommendation: 'Continue leveraging this technique in future engagements'
        });
      });

    const failures = actions.filter(a => {
      const result = (a.result || '').toLowerCase();
      return result === 'failed' || result === 'blocked';
    });
    
    if (failures.length > 0) {
      lessons.challenges.push({
        type: 'detection',
        description: `${failures.length} actions were blocked or failed`,
        recommendation: 'Review and improve evasion techniques'
      });
    }

    const actionsByHour = this.groupActionsByHour(actions);
    const optimalHours = this.findOptimalOperatingHours(actionsByHour);
    
    if (optimalHours.length > 0) {
      lessons.improvements.push({
        type: 'timing',
        description: `Operations were most successful during ${optimalHours.join(', ')} hours`,
        recommendation: 'Consider timing future operations during these windows'
      });
    }

    return lessons;
  },

  generateFutureConsiderations(actions, findings) {
    const considerations = [];

    const usedTactics = new Set();
    actions.forEach(action => {
      if (action.mitreAttackTechnique?.tactic) {
        usedTactics.add(action.mitreAttackTechnique.tactic);
      }
    });

    const allTactics = [
      'Reconnaissance', 'Resource Development', 'Initial Access', 
      'Execution', 'Persistence', 'Privilege Escalation', 
      'Defense Evasion', 'Credential Access', 'Discovery', 
      'Lateral Movement', 'Collection', 'Command and Control', 
      'Exfiltration', 'Impact'
    ];

    const unusedTactics = allTactics.filter(t => !usedTactics.has(t));
    
    if (unusedTactics.length > 0) {
      considerations.push({
        category: 'coverage',
        title: 'Unexplored Attack Vectors',
        description: `The following MITRE ATT&CK tactics were not tested: ${unusedTactics.join(', ')}`,
        recommendation: 'Consider testing these areas in future engagements'
      });
    }

    if (!actions.some(a => a.mitreAttackTechnique?.id === 'T1547')) {
      considerations.push({
        category: 'apt',
        title: 'Advanced Persistence Testing',
        description: 'Limited testing of advanced persistence mechanisms',
        recommendation: 'Test more sophisticated persistence techniques'
      });
    }

    considerations.push({
      category: 'cloud',
      title: 'Cloud Security Assessment',
      description: 'Consider cloud-specific attack scenarios',
      recommendation: 'Include cloud platform testing in scope'
    });

    return considerations;
  },

  generateAppendices(actions, targets) {
    return {
      allActions: this.generateActionTable(actions),
      targetList: this.generateTargetList(targets),
      techniqueMatrix: this.generateTechniqueMatrix(actions),
      evidenceLog: this.generateEvidenceLog(actions),
      toolsList: this.extractToolsList(actions)
    };
  },

  // Helper methods
  calculateDuration(start, end) {
    if (!start) return 'N/A';
    if (!end) return 'Ongoing';
    
    try {
      const startDate = new Date(start);
      const endDate = new Date(end);
      const duration = endDate - startDate;
      
      if (duration < 0) return 'Invalid duration';
      
      const hours = Math.floor(duration / (1000 * 60 * 60));
      const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));
      
      if (hours > 0) {
        return `${hours}h ${minutes}m`;
      }
      return `${minutes}m`;
    } catch (error) {
      return 'Invalid duration';
    }
  },

  calculateTotalDuration(actions) {
    const validActions = actions.filter(a => 
      (a.startTimestamp || a.timestamp) && a.endTimestamp
    );
    
    if (validActions.length === 0) return 'N/A';
    
    try {
      const totalMs = validActions.reduce((sum, action) => {
        const start = new Date(action.startTimestamp || action.timestamp);
        const end = new Date(action.endTimestamp);
        const duration = end - start;
        return sum + (duration > 0 ? duration : 0);
      }, 0);
      
      const hours = Math.floor(totalMs / (1000 * 60 * 60));
      return `${hours} hours`;
    } catch (error) {
      return 'N/A';
    }
  },

  calculateAverageDuration(actions) {
    const validActions = actions.filter(a => 
      (a.startTimestamp || a.timestamp) && a.endTimestamp
    );
    
    if (validActions.length === 0) return 'N/A';
    
    try {
      const totalMs = validActions.reduce((sum, action) => {
        const start = new Date(action.startTimestamp || action.timestamp);
        const end = new Date(action.endTimestamp);
        const duration = end - start;
        return sum + (duration > 0 ? duration : 0);
      }, 0);
      
      const avgMs = totalMs / validActions.length;
      const minutes = Math.floor(avgMs / (1000 * 60));
      return `${minutes} minutes`;
    } catch (error) {
      return 'N/A';
    }
  },

  getMostUsedTechniques(actions) {
    const techniqueCount = {};
    actions.forEach(action => {
      if (action.mitreAttackTechnique) {
        const tech = action.mitreAttackTechnique.id;
        techniqueCount[tech] = (techniqueCount[tech] || 0) + 1;
      }
    });
    
    return Object.entries(techniqueCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([tech, count]) => ({ technique: tech, count }));
  },

  groupByOperator(actions) {
    const operators = {};
    actions.forEach(action => {
      const op = action.operator || 'Unknown';
      if (!operators[op]) {
        operators[op] = { total: 0, successful: 0 };
      }
      operators[op].total++;
      const result = (action.result || '').toLowerCase();
      if (result === 'successful' || result === 'success') {
        operators[op].successful++;
      }
    });
    return operators;
  },

  isActionCritical(action) {
    const criticalTechniques = ['T1003', 'T1078', 'T1486', 'T1490'];
    const result = (action.result || '').toLowerCase();
    return (result === 'successful' || result === 'success') && 
           action.mitreAttackTechnique && 
           criticalTechniques.includes(action.mitreAttackTechnique.id);
  },

  getAffectedTargets(action, allTargets) {
    const affected = [];
    
    if (action.targets && action.targets.length > 0) {
      action.targets.forEach(targetId => {
        const targetIdStr = typeof targetId === 'object' ? targetId._id : targetId;
        const target = allTargets.find(t => 
          t._id === targetIdStr || 
          t._id === targetId ||
          (typeof targetId === 'object' && t._id === targetId._id)
        );
        if (target) {
          affected.push(target);
        }
      });
    }
    
    return affected;
  },

  getRemediationForTechnique(techniqueId) {
    const remediations = {
      'T1003': 'Implement Credential Guard, monitor LSASS access, enable advanced audit policies',
      'T1078': 'Enforce MFA, implement conditional access policies, monitor account usage patterns',
      'T1055': 'Enable exploit protection, implement application whitelisting, monitor process creation',
      'T1021': 'Restrict remote access, implement network segmentation, monitor lateral movement',
      'T1486': 'Implement robust backup strategies, enable ransomware protection, restrict admin privileges'
    };
    
    return remediations[techniqueId] || 'Review MITRE ATT&CK mitigations for this technique';
  },

  getRecommendationsForTechnique(techniqueId) {
    const baseRecs = [
      {
        id: `REC-TECH-${techniqueId}`,
        priority: 'high',
        category: 'technical',
        title: `Mitigate ${techniqueId}`,
        description: this.getRemediationForTechnique(techniqueId),
        effort: 'medium',
        impact: 'high'
      }
    ];
    
    return baseRecs;
  },

  getRecommendationForPattern(pattern) {
    return {
      id: `REC-PAT-${pattern.id}`,
      priority: pattern.severity === 'critical' ? 'immediate' : 'high',
      category: 'strategic',
      title: `Address: ${pattern.title}`,
      description: `Implement controls to prevent ${pattern.description}`,
      effort: 'high',
      impact: 'critical'
    };
  },

  summarizeMainFindings(findings) {
    const criticalCount = findings.critical.length;
    const highCount = findings.high.length;
    
    let summary = '';
    if (criticalCount > 0) {
      summary += `${criticalCount} critical vulnerabilities were identified that could lead to complete system compromise. `;
    }
    if (highCount > 0) {
      summary += `${highCount} high-severity issues were discovered that pose significant risk. `;
    }
    
    return summary || 'The assessment identified several security improvements that should be addressed.';
  },

  assessOverallRisk(findings, statistics) {
    const criticalCount = findings.critical.length;
    const successRate = statistics.successRate;
    
    if (criticalCount > 2 || successRate > 0.8) {
      return {
        level: 'CRITICAL',
        description: 'Immediate action required to address significant security vulnerabilities'
      };
    } else if (criticalCount > 0 || successRate > 0.6) {
      return {
        level: 'HIGH',
        description: 'Significant security improvements needed'
      };
    } else if (findings.high.length > 2 || successRate > 0.4) {
      return {
        level: 'MEDIUM',
        description: 'Several security enhancements recommended'
      };
    }
    
    return {
      level: 'LOW',
      description: 'Security posture is generally good with minor improvements suggested'
    };
  },

  groupActionsByHour(actions) {
    const hourly = {};
    actions.forEach(action => {
      try {
        const hour = new Date(action.startTimestamp || action.timestamp).getHours();
        if (!hourly[hour]) {
          hourly[hour] = { total: 0, successful: 0 };
        }
        hourly[hour].total++;
        const result = (action.result || '').toLowerCase();
        if (result === 'successful' || result === 'success') {
          hourly[hour].successful++;
        }
      } catch (error) {
        // Skip invalid timestamps
      }
    });
    return hourly;
  },

  findOptimalOperatingHours(hourlyStats) {
    return Object.entries(hourlyStats)
      .filter(([hour, stats]) => stats.total > 0 && (stats.successful / stats.total) > 0.7)
      .map(([hour]) => `${hour}:00`)
      .slice(0, 3);
  },

  generateActionTable(actions) {
    return actions.map(action => ({
      timestamp: action.startTimestamp || action.timestamp,
      title: action.title || 'Untitled Action',
      technique: action.mitreAttackTechnique?.id || 'N/A',
      operator: action.operator || 'Unknown',
      result: action.result || 'unknown',
      duration: this.calculateDuration(action.startTimestamp || action.timestamp, action.endTimestamp)
    }));
  },

  generateTargetList(targets) {
    return targets.map(target => ({
      id: target._id,
      type: target.type,
      value: target.value,
      description: target.description || 'N/A',
      compromised: target.compromised || false
    }));
  },

  generateTechniqueMatrix(actions) {
    const matrix = {};
    actions.forEach(action => {
      if (action.mitreAttackTechnique) {
        const tech = action.mitreAttackTechnique.id;
        if (!matrix[tech]) {
          matrix[tech] = {
            name: action.mitreAttackTechnique.name,
            tactic: action.mitreAttackTechnique.tactic,
            attempts: 0,
            successful: 0,
            detected: 0,
            blocked: 0,
            failed: 0,
            partial: 0,
            unknown: 0
          };
        }
        matrix[tech].attempts++;
        const result = (action.result || 'unknown').toLowerCase();
        switch(result) {
          case 'successful':
          case 'success':
            matrix[tech].successful++;
            break;
          case 'detected':
            matrix[tech].detected++;
            break;
          case 'blocked':
            matrix[tech].blocked++;
            break;
          case 'failed':
          case 'failure':
            matrix[tech].failed++;
            break;
          case 'partial':
            matrix[tech].partial++;
            break;
          default:
            matrix[tech].unknown++;
        }
      }
    });
    return matrix;
  },

  generateEvidenceLog(actions) {
    const evidence = [];
    actions.forEach(action => {
      if (action.evidence && action.evidence.length > 0) {
        action.evidence.forEach((item, index) => {
          evidence.push({
            action: action.title || 'Untitled Action',
            timestamp: action.startTimestamp || action.timestamp,
            type: typeof item === 'string' && item.startsWith('data:image') ? 'screenshot' : 'url',
            data: item
          });
        });
      }
    });
    return evidence;
  },

  extractToolsList(actions) {
    const tools = new Set();
    actions.forEach(action => {
      if (action.notes) {
        const commonTools = [
          'nmap', 'metasploit', 'cobalt strike', 'mimikatz', 'bloodhound',
          'powershell', 'psexec', 'wmic', 'crackmapexec', 'responder',
          'impacket', 'empire', 'covenant', 'sliver', 'burp', 'gobuster',
          'hydra', 'john', 'hashcat', 'sqlmap', 'dirb', 'nikto'
        ];
        
        const lowerNotes = action.notes.toLowerCase();
        commonTools.forEach(tool => {
          if (lowerNotes.includes(tool)) {
            tools.add(tool);
          }
        });
      }
    });
    
    return Array.from(tools).sort();
  },

  generateVisualizationNodes(phaseMap, targets) {
    const nodes = [];
    
    // Add phase nodes
    Array.from(phaseMap.values()).forEach((phase, index) => {
      if (phase.actionCount > 0) {
        nodes.push({
          id: `phase-${index}`,
          type: 'phase',
          label: phase.name,
          actionCount: phase.actionCount,
          successRate: phase.actionCount > 0 ? phase.successfulCount / phase.actionCount : 0,
          x: (index % 4) * 200,
          y: Math.floor(index / 4) * 150
        });
      }
    });
    
    // Add target nodes
    targets.forEach((target, index) => {
      nodes.push({
        id: `target-${target._id}`,
        type: 'target',
        label: target.value,
        targetType: target.type,
        x: 800,
        y: index * 50
      });
    });
    
    return nodes;
  },

  generateVisualizationEdges(connections, actions) {
    const edges = [];
    const edgeMap = new Map();
    
    connections.forEach(conn => {
      const key = `${conn.from}-${conn.to}`;
      if (!edgeMap.has(key)) {
        edgeMap.set(key, {
          source: conn.from,
          target: conn.to,
          count: 0,
          successful: 0
        });
      }
      
      const edge = edgeMap.get(key);
      edge.count++;
      
      // Check if the connection resulted in success
      const action = actions.find(a => 
        a.mitreAttackTechnique?.tactic === conn.to && 
        new Date(a.startTimestamp || a.timestamp).getTime() === new Date(conn.timestamp).getTime()
      );
      
      if (action && (action.result === 'successful' || action.result === 'success')) {
        edge.successful++;
      }
    });
    
    // Convert to array
    edgeMap.forEach((edge, key) => {
      edges.push({
        id: key,
        source: edge.source,
        target: edge.target,
        weight: edge.count,
        successRate: edge.successful / edge.count
      });
    });
    
    return edges;
  },

  assessActionSeverity(action) {
    const criticalTechniques = ['T1003', 'T1078', 'T1486', 'T1490'];
    const highTechniques = ['T1055', 'T1543', 'T1574', 'T1098', 'T1021', 'T1053'];
    
    const result = (action.result || '').toLowerCase();
    if (result !== 'successful' && result !== 'success') return 'low';
    
    const techniqueId = action.mitreAttackTechnique?.id;
    if (criticalTechniques.includes(techniqueId)) return 'critical';
    if (highTechniques.includes(techniqueId)) return 'high';
    
    return 'medium';
  },

  // Generate web assessment section from web testing data
  generateWebAssessment(webData) {
    if (!webData || !webData.webTargets || webData.webTargets.length === 0) return null;

    const { webTargets, webFindings } = webData;
    const findings = Array.isArray(webFindings) ? webFindings : [];

    if (findings.length === 0 && webTargets.length === 0) return null;

    // Severity breakdown
    const severityBreakdown = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
    findings.forEach(f => {
      const sev = (f.severity || 'info').toLowerCase();
      if (severityBreakdown[sev] !== undefined) severityBreakdown[sev]++;
    });

    // WSTG coverage
    const wstgCategories = {};
    findings.forEach(f => {
      if (f.wstgTest?.categoryCode) {
        if (!wstgCategories[f.wstgTest.categoryCode]) {
          wstgCategories[f.wstgTest.categoryCode] = { tested: 0, findings: 0 };
        }
        wstgCategories[f.wstgTest.categoryCode].tested++;
        wstgCategories[f.wstgTest.categoryCode].findings++;
      }
    });

    // MITRE cross-reference
    const mitreTechniques = [];
    findings.forEach(f => {
      if (f.mitreTechnique) {
        mitreTechniques.push({
          techniqueId: f.mitreTechnique,
          findingTitle: f.title,
          severity: f.severity,
          wstgTestId: f.wstgTest?.testId
        });
      }
    });

    return {
      targets: webTargets.map(t => ({
        baseUrl: t.baseUrl,
        webType: t.webType,
        target: t.target
      })),
      totalFindings: findings.length,
      severityBreakdown,
      wstgCoverage: wstgCategories,
      findings: findings.map(f => ({
        title: f.title,
        severity: f.severity,
        confidence: f.confidence,
        wstgTestId: f.wstgTest?.testId,
        wstgTestName: f.wstgTest?.testName,
        endpoint: f.endpoint ? `${f.endpoint.method || ''} ${f.endpoint.path || f.endpoint.url || ''}`.trim() : '',
        description: f.description,
        mitreTechnique: f.mitreTechnique
      })),
      mitreCrossReference: mitreTechniques
    };
  },

  generateActionTags(action) {
    const tags = [];
    
    const result = (action.result || '').toLowerCase();
    if (result === 'successful' || result === 'success') tags.push('successful');
    if (result === 'detected') tags.push('detected');
    if (result === 'blocked') tags.push('blocked');
    
    if (action.mitreAttackTechnique) {
      tags.push(action.mitreAttackTechnique.tactic.toLowerCase().replace(/\s+/g, '-'));
    }
    
    if (action.evidence && action.evidence.length > 0) {
      tags.push('has-evidence');
    }
    
    return tags;
  }
};

export default enhancedDebriefGeneratorService;